import React, { Suspense, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import get from 'lodash/get';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import find from 'lodash/find';
import isEmpty from 'lodash/isEmpty';
import toUpper from 'lodash/toUpper';

import {
  ABOUT_PAGE_TEMPLATE,
  EVENT_TEMPLATE,
  FAQ_PAGE_TEMPLATE,
  HEPBNOW_PAGE_TEMPLATE,
  HOME_PAGE_TEMPLATE,
} from 'constants/custom-page';
import HeadTags from 'components/head-tags';
import SkeletonLayout from 'components/skeleton-layout';
import { useGetCustomPageQuery } from 'hooks/custom-page';
import { rootSiteSelector, siteFromRootSiteSelector } from 'selectors/root-site';
import { useSearchObject } from 'hooks/location';
import langActions from 'actions/lang-actions';

const CustomHepbnowPage = React.lazy(() => import('containers/custom-hepbnow-page'));
const StandardHomePage = React.lazy(() => import('components/standard-home-page'));
const About = React.lazy(() => import('containers/about'));
const FAQPage = React.lazy(() => import('containers/faq'));

const CUSTOM_TEMPLATES = {
  [HOME_PAGE_TEMPLATE]: StandardHomePage,
  [ABOUT_PAGE_TEMPLATE]: About,
  [FAQ_PAGE_TEMPLATE]: FAQPage,
  [HEPBNOW_PAGE_TEMPLATE]: CustomHepbnowPage,
  [EVENT_TEMPLATE]: CustomHepbnowPage,
};

const CustomPage = ({ customPageId, params, staticJsonUrl }) => {
  const { siteSlug } = useParams();
  const searchObject = useSearchObject();

  const dispatch = useDispatch();

  const { supportingLanguages } = useSelector(siteSlug ? siteFromRootSiteSelector(siteSlug) : rootSiteSelector);

  const { data, isFetching } = useGetCustomPageQuery(customPageId, params, staticJsonUrl);
  const [template, setTemplate] = useState(HOME_PAGE_TEMPLATE);
  const [pageName, setPageName] = useState();

  const TemplateContainer = useMemo(() => CUSTOM_TEMPLATES[template], [template]);

  useEffect(() => {
    if (data?.customPage) {
      setPageName(get(data, 'name'));
      setTemplate(get(data, 'template', HOME_PAGE_TEMPLATE));
    }
  }, [data]);

  useEffect(() => {
    const lang = find(supportingLanguages, ['symbol', toUpper(searchObject.lang)]);

    lang && dispatch(langActions.updateLanguage(lang.id));
  }, [dispatch, searchObject, supportingLanguages]);

  return isFetching || isEmpty(data) ? (
    <SkeletonLayout showHeader={false} showFooter={false} />
  ) : (
    <>
      <HeadTags title={pageName} />
      <Suspense fallback={<SkeletonLayout showHeader={false} showFooter={false} />}>
        <TemplateContainer customPage />
      </Suspense>
    </>
  );
};

CustomPage.propTypes = {
  customPageId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  params: PropTypes.shape({
    template: PropTypes.string,
  }),
  staticJsonUrl: PropTypes.string,
};

export default CustomPage;
